/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import { DeleteIcon, Search2Icon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import axios from "axios";
import moment from "moment";
import { useMemo, useRef, useState } from "react";
import { useGlobalFilter, usePagination, useTable } from "react-table";
import { toast } from "react-toastify";
import { useConfirmDialog } from "../../../hooks/useConfirmDialog";
import apiClient from "../../../utils/ApiClient/apiClientAxios";
import { toastService } from "../../../utils/ApiClient/toastService";
import CustomModal from "../../Common/CustomModal/CustomModal";
import AddNewCodeForm from "./AddNewCodeForm";

const EditCodeDataTable = ({ codeData, handleEdit, getListCodes }) => {
  const [filterInput, setFilterInput] = useState("");
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [isUpdating, setIsUpdating] = useState(false);
  // Colors (call hooks only at top level)
  const hoverBg = useColorModeValue("gray.100", "gray.700");
  const rowBg = useColorModeValue("white", "gray.800");
  const headerBg = useColorModeValue("gray.100", "gray.900");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const formRef = useRef();
  const { confirm, ConfirmDialog } = useConfirmDialog();

  const handleDeleteRow = async (row) => {
    const ok = await confirm({
      title: "Delete Code",
      body: "Are you sure you want to delete this code? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      confirmColorScheme: "red",
    });

    if (ok) {
      const payload = {
        id: row.original?.id,
        TYPE: "CODE",
        ttl: 90,
      };

      try {
        await axios.post("/api/writeConfig", payload);
        toast.success("Configuration deleted successfully.");
        getListCodes();
      } catch (error) {
        console.error("Delete failed", error);
        toast.error("Failed to delete configuration.");
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Code ID",
        accessor: "id",
      },
      {
        Header: "Modified Time",
        accessor: (row) => moment.unix(row._ts).format("YYYY-MM-DD HH:mm:ss"),
      },
      {
        Header: "Description",
        accessor: "DESCRIPTION",
      },
      {
        Header: "Modified By",
        accessor: "MODIFIED_BY",
      },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <Box
            w={8}
            h={8}
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="full"
            // bg="red.100"
            _hover={{
              bg: "red.100",
            }}
          >
            <DeleteIcon
              size={16}
              cursor="pointer"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteRow(row);
              }}
              color="red"
            />
          </Box>
        ),
      },
    ],
    [],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setGlobalFilter,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data: codeData,
      initialState: { pageSize: 10 },
    },
    useGlobalFilter,
    usePagination,
  );

  const handleSave = async (options) => {
    const cleanedOptions = options?.options.map((option) => {
      return Object.entries(option).reduce((acc, [key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {});
    });
    const ConfigID = options.codeId.replace(/\s+/g, "_").toUpperCase();
    const payload = {
      id: ConfigID,
      CONFIG_ID: ConfigID,
      TYPE: "CODE",
      CONFIG: cleanedOptions,
      DESCRIPTION: options?.description,
    };

    try {
      // Call API
      const response = await apiClient.post("/writeConfig", payload);
      toastService.success(response.data);
      onClose(); // Close modal after save
      getListCodes();
    } catch (error) {
      console.error("Save failed", error);
      toast.error("Failed to save configuration.");
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value || undefined;
    setGlobalFilter(value);
    setFilterInput(value);
  };

  const handleUpdateCache = async () => {
    try {
      setIsUpdating(true);
      const { data } = await apiClient.post("/version");
      console.log("Version updated:", data);
      return data;
    } catch (error) {
      console.error("Failed to update cache version:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Box>
      <Box p={2} borderRadius="md" boxShadow="lg">
        {/* Top Bar */}
        <Flex mb={4} justify="space-between" align="center">
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <Search2Icon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search codes..."
              value={filterInput}
              onChange={handleSearchChange}
              width="300px"
              bg={rowBg}
              borderRadius="md"
              _focus={{ ring: "none", outline: "none" }}
            />
          </InputGroup>
          <Button
            size="sm"
            onClick={handleUpdateCache}
            variant="primaryButton"
            mr={2}
            px={5}
            isLoading={isUpdating}
          >
            Update Cache
          </Button>
          <Button size="sm" onClick={onOpen}>
            Add New
          </Button>
        </Flex>

        {/* Table */}
        <TableContainer
          maxH="62vh"
          overflowY="auto"
          border="2px solid"
          borderColor={borderColor}
          borderRadius="lg"
        >
          <Table
            {...getTableProps()}
            colorScheme="gray"
            variant="unstyled"
            size="sm"
          >
            <Thead>
              {headerGroups.map((headerGroup) => (
                <Tr
                  {...headerGroup.getHeaderGroupProps()}
                  bg={headerBg}
                  position="sticky"
                  top={0}
                  zIndex={1}
                  boxShadow="sm"
                >
                  {headerGroup.headers.map((column) => (
                    <Th
                      {...column.getHeaderProps()}
                      textTransform="uppercase"
                      fontSize="xs"
                      color={textColor}
                    >
                      {column.render("Header")}
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {page.length > 0 ? (
                page.map((row) => {
                  prepareRow(row);

                  return (
                    <Tr
                      {...row.getRowProps()}
                      _hover={{ bg: hoverBg, cursor: "pointer" }}
                      onClick={() => {
                        handleEdit(row.original.id);
                      }}
                    >
                      {row.cells.map((cell) => (
                        <Td {...cell.getCellProps()}>{cell.render("Cell")}</Td>
                      ))}
                    </Tr>
                  );
                })
              ) : (
                <Tr>
                  <Td colSpan={columns.length}>
                    <Text textAlign="center" color="red.500">
                      No Data Found!
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
        {/* Pagination */}
        <Flex justify="space-between" align="center" mt={4}>
          <Text fontSize="sm" color="gray.600">
            Page {pageIndex + 1} of {pageOptions.length}
          </Text>
          <Box>
            <Button
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              size="sm"
              mr={2}
              colorScheme="teal"
              variant="outline"
            >
              Previous
            </Button>
            <Button
              onClick={() => nextPage()}
              disabled={!canNextPage}
              size="sm"
              colorScheme="teal"
            >
              Next
            </Button>
          </Box>
        </Flex>
      </Box>
      {ConfirmDialog}
      {/* ======================for add new */}
      <CustomModal
        size="5xl"
        isCentered={false}
        isOpen={isOpen}
        onClose={onClose}
        bodyContent={
          <Box h="80vh">
            <AddNewCodeForm onSubmit={handleSave} formRef={formRef} />
          </Box>
        }
        footerContent={
          <Box display="flex" alignItems="center" gap={2}>
            <Button onClick={onClose}>Discard</Button>
            <Button
              //   isLoading={isSavingEditedCode} // 👈 Correct prop okay
              loadingText="Saving..." // 👈 Optional
              variant="primary"
              onClick={() => {
                if (formRef.current) {
                  formRef.current.submit();
                }
              }}
            >
              Save
            </Button>
          </Box>
        }
      />
    </Box>
  );
};

export default EditCodeDataTable;
